/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FormEvent, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../store/store'
import {
  FooterFirstSection,
  FooterSecondSection,
  ForgotPasswordButton,
  LoginErrorContainer,
  LoginErrorText,
  LoginFormBody,
  LoginFormFooter,
  SignupLinkButton,
  DivButton,
} from './style'
import {
  resendConfirmSignUp,
  signIn,
} from '../../../../../services/AuthService'
import {
  resetUser,
  setIsCognitoSignedIn,
  setToken,
  userLogin,
} from '../../../../../store/reducers/userReducer'
import { ESignupFormSteps } from '../../../../../types/enums/ESignupFormSteps'
import { FormTypes } from 'types/enums/EFormTypes'
import { PasswordInput } from 'shared/components/password-input/PasswordInput'
import { setFormTranslatedText } from 'pages/auth/utils/setFormTranslatedText'
import { useTranslation } from 'react-i18next'
import { setForm } from 'store/reducers/layoutReducer'
import { EmailInput } from 'shared/components/email-input/EmailInput'
import { useNavigate } from 'react-router-dom'
import { EAppRoutes } from 'types/enums/EAppRoutes'
import { EExternalLinks } from 'types/enums/EExternalLinks'
import { SupportIcon } from 'shared/components/support/style'
import blackWhatsappIcon from 'assets/icons/black-whatsapp.svg'

type LoginFormData = {
  email: string
  password: string
}
type LoginFormDataErrors = Partial<Record<keyof LoginFormData, boolean>>

export const LoginForm: React.FC = () => {
  const isFanzaErrorExists = useAppSelector(
    (state) => state.user.isFanzaErrorExists,
  )
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [loginFormData, setLoginFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  })
  const [errors, setErrors] = useState<LoginFormDataErrors>({
    email: false,
    password: false,
  })
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [loginError, setLoginError] = useState<boolean>(false)

  const { t } = useTranslation('', {
    keyPrefix: 'pages.login',
  })
  setFormTranslatedText('login')

  useEffect(() => {
    if (
      loginFormData.email &&
      loginFormData.password &&
      !errors.email &&
      !errors.password
    ) {
      setIsDisabled(false)
    } else if (!isDisabled) {
      setIsDisabled(true)
    }
  }, [loginFormData, errors, isDisabled])

  const handleEmailChange = (value: string, error?: boolean) => {
    setErrors({ ...errors, email: error })
    setLoginFormData({ ...loginFormData, email: value })
  }

  const handlePasswordChange = (value: string, error?: boolean) => {
    setErrors({ ...errors, password: error })
    setLoginFormData({ ...loginFormData, password: value })
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()

    try {
      dispatch(setIsCognitoSignedIn(false))
      const user = await signIn(loginFormData.email, loginFormData.password)

      if (user) {
        dispatch(
          userLogin({
            password: loginFormData.password,
            email: loginFormData.email,
          }),
        )

        if ('error' in user) {
          await resendConfirmSignUp(loginFormData.email)
          dispatch(
            setForm({
              formStep: ESignupFormSteps.confirmation,
              email: loginFormData.email,
            }),
          )
          navigate(EAppRoutes.SIGN_UP)
          return
        }
        dispatch(setIsCognitoSignedIn(true))
        dispatch(setToken(user.token))
      } else {
        setLoginError(true)
      }
    } catch (err: any) {
      console.log({ err })
      setLoginError(true)
    }
  }

  const navigateToSignUp = () => {
    dispatch(resetUser())
    dispatch(
      setForm({
        formStep: ESignupFormSteps.verificationOption,
      }),
    )
    navigate(EAppRoutes.SIGN_UP)
  }

  return (
    <>
      <LoginFormBody onSubmit={handleSubmit}>
        <EmailInput
          value={loginFormData.email}
          callback={handleEmailChange}
          hasError={errors.email}
          labelText={t('inputs.email.labelText')}
          errorText={t('inputs.email.errorText')}
        />
        <PasswordInput
          hasError={errors.password}
          labelText={t('inputs.password.labelText')}
          errorText={t('inputs.password.errorText')}
          callback={handlePasswordChange}
          className="passwordInput"
        />
        <ForgotPasswordButton
          className={`forgotPasswordButton ${loginError ? 'no-margin' : ''}`}
          onClick={() =>
            dispatch(
              setForm({
                formType: FormTypes.FORGOT_PASSWORD,
                email: loginFormData.email,
              }),
            )
          }
        >
          {t('forgotPassword')}
        </ForgotPasswordButton>
        {loginError && (
          <LoginErrorContainer>
            <LoginErrorText>{t('buttons.submit.errorText')}</LoginErrorText>
          </LoginErrorContainer>
        )}
        {isFanzaErrorExists && (
          <LoginErrorContainer>
            <LoginErrorText>
              {t('buttons.submit.fanzaErrorText')}
            </LoginErrorText>
          </LoginErrorContainer>
        )}
        <DivButton
          onClick={(e) => !isDisabled && handleSubmit(e)}
          className={`div-button ${isDisabled ? 'disabled' : ''}`}
        >
          {t('buttons.submit.text')}
        </DivButton>
      </LoginFormBody>
      <LoginFormFooter>
        <FooterFirstSection>
          {t('signUp.text')}
          <SignupLinkButton className="bold" onClick={navigateToSignUp}>
            {t('signUp.linkText')}
          </SignupLinkButton>
        </FooterFirstSection>

        <FooterSecondSection>
          {t('signUp.deleteUserText')}
          <SupportIcon
            src={blackWhatsappIcon}
            onClick={() => window.open(EExternalLinks.HJBC_WHATSAPP)}
          ></SupportIcon>
        </FooterSecondSection>
      </LoginFormFooter>
    </>
  )
}
